import { useAction, useAtom } from '@reatom/npm-react'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import styled, { css } from 'styled-components'

import { AsyncStateN } from '@blade/std-lib'
import { Button, CircularLoader } from '@blade/ui-kit'
import { Refresh2Icon } from '@blade/ui-kit'
import { DomUtil } from '@root/shared'
import { Separator } from '@root/shared/ui-kit'
import { mediaMaxMobile } from '@root/shared/ui-kit/theme'

import { reatomSplashPointsInfo } from '../atoms'

import BG_BOTTOM_MOBILE from './../assets/bg-bottom-mobile.webp'
import BG_BOTTOM from './../assets/bg-bottom.svg'
import BG_OVERLAY_TOKENS_MOBILE from './../assets/bg-overlay-tokens-mobile.webp'
import BG_OVERLAY_TOKENS from './../assets/bg-overlay-tokens.webp'
import BG_TOP from './../assets/bg-top.webp'
import BG_UNDERLAY_TOKENS_MOBILE from './../assets/bg-underlay-tokens-mobile.webp'
import BG_UNDERLAY_TOKENS from './../assets/bg-underlay-tokens.webp'
import LOGO from './../assets/deluge-logo.svg'
import { SplashPointsCard as Card } from './splash-points-card'
import { SplashPointsCards } from './splash-points-cards'
import { SplashPointsCurrency } from './splash-points-currency'

import type { CurrencyModel } from '@root/entities/currency'

type SplashPointsInfoProps = {
  currency: CurrencyModel
}

export const SplashPointsInfo: React.FC<SplashPointsInfoProps> = ({
  currency,
}) => {
  const { t } = useTranslation()

  const model = useMemo(
    () =>
      // eslint-disable-next-line @reatom/unit-naming-rule
      reatomSplashPointsInfo(currency),
    [currency],
  )
  const [state] = useAtom(model)
  const retry = useAction(model.refresh)

  const renderContent = () =>
    AsyncStateN.matchWaiting(state, {
      Success: (x) => (
        <div>
          <SplashPointsCurrency
            splashPointsAmount={x.splashPointsAmount}
            currency={currency}
            tier={x.tier}
          />
          <SplashPointsCards
            breakdown={x.splashPointsBreakdown}
            valuesBreakdown={x.valuesBreakdown}
          />
          <Disclaimer>{t('splash-points.logarithm-disclaimer')}</Disclaimer>
        </div>
      ),
      Waiting: () => (
        <EmptyContainer>
          <Card>
            <LoadingContainer>
              <span>{t('splash-points.loading')}</span>
              <CircularLoader size={16} />
            </LoadingContainer>
          </Card>
        </EmptyContainer>
      ),
      Error: () => (
        <EmptyContainer>
          <Card
            css={css`
              display: grid;
              gap: 10px;
              justify-content: center;
              place-content: center;
              justify-items: center;
            `}
          >
            <LoadingContainer>
              <span>{t('splash-points.error')}</span>
            </LoadingContainer>
            <Separator
              css={css`
                width: 100%;
              `}
            />
            <Button
              variant="ghost"
              size="xs"
              radius="rounded"
              onClick={() => retry()}
              iconLeft={<Refresh2Icon />}
            >
              {t('common.retry')}
            </Button>
          </Card>
        </EmptyContainer>
      ),
    })

  return (
    <Root>
      <ContentContainer>
        <BgTop />
        <BgBottom />
        <BgUnderlayTokens />
        <Content>
          <Title>{t('splash-points.title')}</Title>
          <Description>
            {t('splash-points.description')}
            <div>{t('splash-points.description-factors')}</div>
          </Description>
          {renderContent()}
          <LogoContainer>
            <Logo />
          </LogoContainer>
        </Content>
      </ContentContainer>
      <BgOverlayTokens />
    </Root>
  )
}

const Root = styled.div`
  position: relative;
  overflow: auto;
`

const ContentContainer = styled.div`
  padding: 100px 32px 32px;
  min-height: 100%;
  width: 100%;
  display: grid;
  grid-template-rows: repeat(auto-fit, auto) 1fr;
  align-items: stretch;
  align-content: stretch;
  justify-items: stretch;
  position: relative;

  ${mediaMaxMobile(css`
    padding-block: 90px 44px;
    width: 100%;
  `)}
`

const Content = styled.div`
  position: relative;

  ${mediaMaxMobile(css`
    padding-inline: 12px;
  `)}

  display: grid;
  align-items: stretch;
  align-content: stretch;
  grid-template-rows: auto auto 1fr auto;
  justify-content: center;
  justify-items: center;
`

const BgTop = styled.div`
  background-image: ${DomUtil.cssUrl(BG_TOP)};
  background-position-y: 0;
  background-repeat: no-repeat;
  background-position-x: center;
  pointer-events: none;
  position: absolute;
  inset: 0;
`

const BgBottom = styled.div`
  background-image: ${DomUtil.cssUrl(BG_BOTTOM)};
  background-position-y: 100%;
  background-repeat: no-repeat;
  background-position-x: center;
  pointer-events: none;
  position: absolute;
  inset: 0;

  ${mediaMaxMobile(css`
    background-image: ${DomUtil.cssUrl(BG_BOTTOM_MOBILE)};
  `)}
`

const BgUnderlayTokens = styled.div`
  background: ${DomUtil.cssUrl(BG_UNDERLAY_TOKENS)};
  background-position-y: 212px;
  background-repeat: no-repeat;
  background-position-x: center;
  pointer-events: none;
  position: absolute;
  inset: 0;

  ${mediaMaxMobile(css`
    background-image: ${DomUtil.cssUrl(BG_UNDERLAY_TOKENS_MOBILE)};
    background-position-y: 0;
  `)}
`

const BgOverlayTokens = styled.div`
  background: ${DomUtil.cssUrl(BG_OVERLAY_TOKENS)};
  background-position-y: 226px;
  background-repeat: no-repeat;
  background-position-x: center;
  pointer-events: none;
  position: absolute;
  inset: 0;

  ${mediaMaxMobile(css`
    background-image: ${DomUtil.cssUrl(BG_OVERLAY_TOKENS_MOBILE)};
    background-position-y: 190px;
  `)}
`

const Title = styled.div`
  color: var(--text-12);
  text-align: center;

  font-size: 28px;
  font-weight: 700;
  line-height: normal;

  margin-bottom: 8px;

  ${mediaMaxMobile(css`
    font-size: 18px;

    margin-bottom: 0px;
  `)}
`

const Description = styled.div`
  color: var(--text-9);
  text-align: center;

  font-size: 14px;
  font-weight: 500;
  line-height: 21px;
  margin: 24px 0;

  & div {
    color: var(--text-12);
    font-weight: 700;
  }

  ${mediaMaxMobile(css`
    font-size: 12px;
    font-weight: 500;
    line-height: 16px;
    margin-bottom: 22px;
  `)}
`

const Disclaimer = styled.div`
  color: var(--text-12);
  text-align: center;

  font-size: 11px;
  font-weight: 700;
  line-height: normal;

  margin-bottom: 39px;

  ${mediaMaxMobile(css`
    line-height: 16px;
  `)}
`

const Logo = styled.div`
  background: ${DomUtil.cssUrl(LOGO)};
  background-size: contain;
  width: 129px;
  height: 42px;

  ${mediaMaxMobile(css`
    width: 110px;
    height: 36px;
    bottom: 44px;
  `)}
`

const LogoContainer = styled.div`
  display: grid;
  align-items: start;
  justify-content: center;
`

const EmptyContainer = styled.div`
  height: 100%;
  display: grid;
  align-items: center;
  justify-content: center;
`

const LoadingContainer = styled.div`
  font-size: 14px;
  font-weight: 500;

  display: grid;
  grid-auto-flow: column;
  align-items: center;
  text-align: center;
  gap: 10px;
`
